/**
 * useTutorStudentsDesk — « Mes élèves » hors live dans le cadre du live (`<TutorStudentsDeskPage>`), à partir de
 * 1 024 px : ce que chaque colonne reçoit, composé des crochets qui existent déjà.
 *
 *  - **L'élève regardé** est un état d'écran ; il survit au changement d'onglet de sa fiche, et son activité ouverte
 *    se referme quand il change.
 *  - **Sa fiche** est celle de « Mes élèves » (`useTutorStudentProfile`), au centre tant qu'aucune activité n'est
 *    ouverte. Ses activités, touchées dans l'onglet Activités ou dans la liste de gauche, s'ouvrent au centre, et sa
 *    conversation passe dans la colonne du champion (« Messages »), lue et marquée lue quand elle s'y voit. Un élève
 *    dont des messages attendent ouvre la colonne sur « Messages ».
 *  - **Sa liste d'activités** (la gauche) et **sa tête au centre** suivent les règles du live
 *    (`tutorStudentsDeskViewModel`) ; « À faire » se range comme dans la fiche (`useTutorStudentActivitiesReorder`).
 *  - **L'activité ouverte** est lue et décidée par la fiche d'activité hors live (`useTutorActivityDetails` : les
 *    mêmes appels, les mêmes questions), montrée par le centre du live (`tutorLiveCenterFocus`).
 *  - **La colonne du champion** génère par le crochet du live (`useTutorLiveFeedGeneration`), sans salle : ni séance, ni
 *    message à l'élève (`send` nul). Ses bulles se gardent comme en live, côté navigateur, sous la clé
 *    `compte·hors-live` (`data.tutorLiveSession`). La relecture des propositions se pose au centre. Après une
 *    création, la fiche de l'élève se relit : sa liste montre l'activité née.
 *
 * Hors du cadre (sous 1 024 px), rien ne lit ni n'écrit : la fiche est poussée par l'écran, comme aujourd'hui.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { cleanMediaUrl } from "../../../../../../data/constants/apiConstants"
import { apiRequest } from "../../../../../../data/redux/actions/api"
import { tutorLiveSessionKey } from "../../../../../../data/redux/reducers/data/tutorLiveSession"
import { studentsService } from "../../../../../../data/services/studentsService"
import { Snackbar } from "../../../../../../design-system"
import { TutorActivityDecisions } from "../../../../../../design-system/tutor"
import {
    type TutorStudentsDeskCenterZone,
    type TutorStudentsDeskPageProps,
    type UITutorStudentsDeskSamTab,
} from "../../../../../../design-system/pages/TutorStudentsDeskPage/TutorStudentsDeskPage"
import { type ApiSchoolSubject, indexSubjectsById } from "../../../../adapters/parent/shared/schoolSubjectAdapter"
import { LIVE_HELPER_SLUG, tutorHelperName } from "../../../../adapters/tutor/Live/tutorLiveRoomAdapter"
import { prerequisiteQuizAvailable } from "../../../../adapters/tutor/Students/tutorGenerateActivityAdapter"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"
import {
    defaultCenterTab,
    liveActivityInfo,
    tutorLiveCenterFocus,
} from "../../../../view-models/tutor/Live/tutorLiveCenterViewModel"
import { tutorLiveFeedView } from "../../../../view-models/tutor/Live/tutorLiveFeedViewModel"
import { makeTutorStudentsDeskViewModel } from "../../../../view-models/tutor/Students/tutorStudentsDeskViewModel"
import { type UITutorLiveCenterTab } from "../../../types/tutorLive"
import { type UITutorStudent, type UITutorStudentTab } from "../../../types/tutorEleves"
import { useTutorLiveFeedGeneration } from "../Room/useTutorLiveFeedGeneration"
import { useTutorLiveFeedSheet } from "../Room/useTutorLiveFeedSheet"
import { type OpenGeneration, type TutorGenerateTarget, type TutorStudentId } from "./tutorStudentTabs"
import { useTutorActivityDetails } from "./useTutorActivityDetails"
import { REORDER_FAILED } from "./useTutorStudentActivitiesTab"
import { useTutorStudentActivitiesReorder } from "./useTutorStudentActivitiesReorder"
import { useTutorStudentProfile } from "./useTutorStudentProfile"
import { WITH_SAM_MISSION } from "./useTutorStudentProfileData"

/** La mémoire de la colonne du champion hors live : une seule par compte, à côté de celles des séances. */
const OFF_LIVE_SESSION = "hors-live"

export type UseTutorStudentsDeskArgs = {
    /** Le cadre se voit (la largeur le permet). Caché, rien ne se lit. */
    on: boolean
    /** Les élèves de « Mes élèves », dans leur ordre. */
    students: UITutorStudent[]
    /** Le parcours de génération d'aujourd'hui (plusieurs élèves, un fichier, « à partir de cet exo »). */
    openGeneration?: OpenGeneration
}

export type TutorStudentsDesk = {
    /** Les props de la page, hors de la liste des élèves que l'écran tient. */
    page: Pick<TutorStudentsDeskPageProps, "activities" | "center" | "sam" | "helper">
    selectedId: TutorStudentId | null
    select: (student: UITutorStudent) => void
    /** Ses non-lus touchés : l'élève, et sa conversation dans la colonne du champion. */
    openMessages: (student: UITutorStudent) => void
    overlay: React.ReactNode
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type State = any

type SamColumn = { tab: UITutorStudentsDeskSamTab; forUnread: boolean }
const chosen = (tab: UITutorStudentsDeskSamTab): SamColumn => ({ tab, forUnread: false })
const FOR_UNREAD: SamColumn = { tab: "messages", forUnread: true }

export function useTutorStudentsDesk(args: UseTutorStudentsDeskArgs): TutorStudentsDesk {
    const { on, students, openGeneration } = args
    const dispatch = useDispatch()
    const [selectedId, setSelectedId] = useState<TutorStudentId | null>(null)
    const [ficheTab, setFicheTab] = useState<UITutorStudentTab>("overview")
    // L'onglet de la colonne du champion, et s'il s'est ouvert pour des non-lus (`select`) : ouvert pour eux, il rend la
    // main au champion sur l'élève suivant s'il n'a rien écrit ; choisi par le masteur, il reste.
    const [sam, setSam] = useState(chosen("sam"))
    const samTab = sam.tab
    const changeSamTab = useCallback((tab: UITutorStudentsDeskSamTab) => setSam(chosen(tab)), [])
    const [expandedDone, setExpandedDone] = useState(false)
    const [center, setCenter] = useState<{ tab: UITutorLiveCenterTab | null; split: boolean }>({
        tab: null,
        split: true,
    })
    const studentId = on ? selectedId : null
    const targetId = studentId != null ? String(studentId) : null

    // ── L'élève regardé ──────────────────────────────────────────────────────────────────────────────
    const viewModel = useMemo(makeTutorStudentsDeskViewModel, [])
    const vm = useSelector((state: State) => viewModel(state, studentId, expandedDone))
    const firstname = vm.student?.firstname ?? ""

    // ── La colonne du champion : la génération du live, sans salle ────────────────────────────────────────
    const accountId = useSelector((state: State) => selectAccount(state)?.id as number | string | undefined)
    const memoryKey = tutorLiveSessionKey(accountId, OFF_LIVE_SESSION) as string | null
    const memory = useSelector((state: State) =>
        memoryKey != null ? state?.data?.tutorLiveSession?.sessions?.[memoryKey] : undefined
    )
    // Le quiz de prérequis hors live : la règle du parcours d'aujourd'hui (la salle de sa première séance).
    const quizAvailable = useSelector((state: State) => prerequisiteQuizAvailable(selectAccount(state)))
    const studentIds = useMemo(() => students.map((student) => String(student.id)), [students])
    const firstnameOf = useCallback(
        (id: string) => (id === targetId ? firstname : (students.find((one) => String(one.id) === id)?.name ?? "")),
        [firstname, students, targetId]
    )
    const [notice, setNotice] = useState<{ message: string; tone: "success" | "danger" | "info" } | null>(null)
    const notify = useCallback(
        (message: string, tone: "success" | "danger" | "info") => setNotice({ message, tone }),
        []
    )
    const generation = useTutorLiveFeedGeneration({
        targetId,
        studentIds,
        firstnameOf,
        liveSessionId: null,
        quizAvailable,
        send: null,
        memoryKey,
        helperName: tutorHelperName(),
        notify,
        watching: targetId,
        presenting: false,
        similarSource: () => null,
        roomDrop: false,
        offLive: true,
    })
    const { editor, sheet } = useTutorLiveFeedSheet({ generation, preview: null })

    // Une création finie (la tranche de `createActivityAsTutor` revient au repos) : l'élève se relit, sa liste et sa
    // mission avec.
    const wasCreating = useRef(false)
    useEffect(() => {
        if (wasCreating.current && !generation.creating && studentId != null) {
            dispatch(apiRequest(studentsService.getStudent, { id: studentId }, {}, WITH_SAM_MISSION) as never)
        }
        wasCreating.current = generation.creating
    }, [dispatch, generation.creating, studentId])

    const { request } = generation
    // Depuis « Messages », le champ n'est monté qu'au rendu suivant : la demande attend l'onglet du champion, sinon son
    // focus tombe sur un champ qui n'existe pas encore.
    const [composerAsked, setComposerAsked] = useState<{ target: string } | null>(null)
    const openComposer = useCallback((id: TutorStudentId) => {
        setSam(chosen("sam"))
        setComposerAsked({ target: String(id) })
    }, [])
    useEffect(() => {
        if (composerAsked == null || samTab !== "sam") return
        request(composerAsked)
        setComposerAsked(null)
    }, [composerAsked, request, samTab])
    // « Activité » de la fiche ouvre le champ du champion ; le reste garde le parcours d'aujourd'hui.
    const deskGeneration = useCallback<OpenGeneration>(
        (target: TutorGenerateTarget) => {
            if (target.kind === "student") return openComposer(target.studentId)
            openGeneration?.(target)
        },
        [openComposer, openGeneration]
    )

    // ── L'activité ouverte, au centre ────────────────────────────────────────────────────────────────
    const details = useTutorActivityDetails({ studentId, openGeneration: deskGeneration })
    const openActivity = details.open
    const onOpenActivity = useCallback(
        (activityId: TutorStudentId) => {
            setCenter({ tab: null, split: true })
            openActivity(activityId)
        },
        [openActivity]
    )
    const closeActivity = details.modal.onClose
    // Une activité qui ne se lit pas ne s'ouvre pas au centre (il attend sa lecture) : la panne se dit, et la
    // ligne touchée se rouvre d'un geste.
    const loadError = details.modal.error
    useEffect(() => {
        if (loadError == null) return
        notify(loadError, "danger")
        closeActivity()
    }, [closeActivity, loadError, notify])

    // ── Sa fiche ─────────────────────────────────────────────────────────────────────────────────────
    const fiche = useTutorStudentProfile({
        studentId,
        tab: ficheTab,
        onChangeTab: setFicheTab,
        visible: studentId != null,
        openGeneration: deskGeneration,
        onOpenActivity,
        messagesActive: samTab === "messages",
        withSamMission: true,
    })

    const reorder = useTutorStudentActivitiesReorder(studentId)
    // Un rangement refusé reprend l'ordre du serveur et le dit, comme l'onglet Activités de la fiche.
    const { failure: reorderFailure, dismiss: dismissReorder } = reorder
    useEffect(() => {
        if (reorderFailure == null) return
        notify(REORDER_FAILED[reorderFailure], "danger")
        dismissReorder()
    }, [dismissReorder, notify, reorderFailure])

    // Un élève dont des messages attendent s'ouvre sur sa conversation : « À traiter » d'abord, comme en live.
    const select = useCallback(
        (student: UITutorStudent) => {
            const other = String(student.id) !== String(selectedId)
            const unread = (student.unreadMessagesCount ?? 0) > 0
            setSam((was) => (unread ? FOR_UNREAD : other && was.forUnread ? chosen("sam") : was))
            if (!other) return
            setSelectedId(student.id)
            setExpandedDone(false)
            closeActivity()
        },
        [closeActivity, selectedId]
    )
    // La bulle de la colonne n'y vient qu'avec des non-lus : la toucher, c'est toucher la ligne.
    const openMessages = select

    // ── Ce que chaque colonne reçoit ─────────────────────────────────────────────────────────────────
    const raw = details.raw
    const subjects = useSelector(
        (state: State) => state?.data?.staticData?.school_subjects as ApiSchoolSubject[] | undefined
    )
    const subjectName = useMemo(
        () =>
            raw?.school_subject_id == null
                ? null
                : (indexSubjectsById(subjects)[String(raw.school_subject_id)]?.name ?? null),
        [raw?.school_subject_id, subjects]
    )
    const focus = useMemo(
        () =>
            studentId != null && raw != null && details.activityId != null
                ? tutorLiveCenterFocus({
                      student: { id: Number(studentId), activities: [raw] },
                      activityId: String(details.activityId),
                      seenActivityId: null,
                      online: false,
                      cleanUrl: cleanMediaUrl,
                      correctionReview: details.modal.correctionFeedback?.value ?? null,
                      quizFeedback: details.modal.quizFeedback?.value ?? null,
                      subjectName,
                  })
                : null,
        [
            details.activityId,
            details.modal.correctionFeedback?.value,
            details.modal.quizFeedback?.value,
            raw,
            studentId,
            subjectName,
        ]
    )
    let centerZone: TutorStudentsDeskCenterZone
    if (studentId == null) centerZone = { kind: "empty" }
    else if (vm.student == null) centerZone = { kind: "loading" }
    else {
        // Sans onglet choisi : le rendu de l'activité ouverte, comme en live ; sans activité, sa fiche.
        const tab =
            center.tab ?? (focus != null ? defaultCenterTab(focus.work != null, focus.quiz != null).tab : "profile")
        const modal = details.modal
        // La fiche au centre, sans « Messages » : la conversation est dans la colonne du champion.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { messages: _messages, ...profile } = fiche.page
        centerZone = {
            kind: "student",
            student: vm.student,
            center: {
                tab,
                onChangeTab: (next) => setCenter((was) => ({ ...was, tab: next })),
                split: center.split,
                onToggleSplit: () => setCenter((was) => ({ ...was, split: !was.split })),
                profile,
                focus,
                onCloseActivity: closeActivity,
                actions: modal.actions,
                onAction: modal.onAction,
                submitting: modal.submitting,
                activityInfo: liveActivityInfo(raw, subjectName),
                quizFirst: modal.quizFirst,
                activityHandlers: {
                    onCorrectionReview: modal.correctionFeedback?.onChange,
                    onQuizFeedback: modal.quizFeedback?.onChange,
                    onSignalQuestion: modal.onSignalQuizQuestion,
                },
                gestures: {
                    onOpenActivity: (_studentId, activityId) => onOpenActivity(activityId),
                    onAddActivity: openComposer,
                    // Son visage ramène à sa fiche, comme « Son profil » en live.
                    onOpenProfile: () => setCenter((was) => ({ ...was, tab: "profile" })),
                },
            },
        }
    }

    // Hors live, ni signal ni carte : l'heure ne fait rien bouger au fil, il ne se refait qu'à ce qu'il lit.
    const avatarUrl = vm.student?.avatarUrl
    const hasStudent = vm.student != null
    const records = memory?.entries
    const view = useMemo(
        () =>
            targetId != null
                ? tutorLiveFeedView({
                      cards: [],
                      records: records ?? [],
                      transient: generation.transient,
                      students: hasStudent ? [{ id: targetId, firstname, avatarUrl }] : [],
                      selectedId: targetId,
                      withHelper: [],
                      seen: {},
                      now: Date.now(),
                  })
                : null,
        [avatarUrl, firstname, generation.transient, hasStudent, records, targetId]
    )
    const selectedRow = students.find((student) => String(student.id) === targetId)

    const page: TutorStudentsDesk["page"] = {
        activities:
            vm.student != null
                ? {
                      student: { firstname, avatarUrl: vm.student.avatarUrl },
                      list: vm.activities,
                      moreDone: vm.moreDone,
                      openActivityId: details.activityId != null ? String(details.activityId) : undefined,
                      onOpenActivity,
                      onReorder: reorder.reorder,
                      onSeeMoreDone: () => setExpandedDone(true),
                  }
                : null,
        center: centerZone,
        sam: {
            tab: samTab,
            onChangeTab: changeSamTab,
            session: view != null && vm.student != null ? view.session : null,
            mission: vm.mission,
            composer:
                generation.composer != null && editor != null
                    ? { ...generation.composer, editor }
                    : generation.composer,
            onResult: generation.onResult,
            onGeneration: (_entryId, action) => {
                if (action === "review" || action === "sendFirst") generation.onReady(action)
            },
            onPeek: generation.onPeek,
            review: generation.review,
            peek: generation.peek,
            configurator: generation.configurator,
            messages: fiche.page.messages ?? null,
            // Sa conversation lue dans la colonne : `getMarkRead` remet son compte à zéro dans le store, comme sur la ligne.
            unread: selectedRow?.unreadMessagesCount ?? 0,
        },
        helper: LIVE_HELPER_SLUG,
    }

    const overlay = on ? (
        <>
            {fiche.overlay}
            {sheet}
            <TutorActivityDecisions {...details.modal} />
            <Snackbar {...details.notice} />
            <Snackbar
                visible={notice != null}
                message={notice?.message ?? ""}
                tone={notice?.tone}
                onDismiss={() => setNotice(null)}
            />
        </>
    ) : null

    return { page, selectedId, select, openMessages, overlay }
}
